import { useQuery } from "@tanstack/react-query";
import { dropinApi } from "@bw/shared/api";
import type { DropInSessionResponse } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";

const FOREGROUND_REFRESH_MS = 15_000;
const MAX_TIMEOUT_MS = 2_147_483_647;

async function fetchDropInSession(
  facilityId: string,
  refreshMode: NonNullable<UseDropInSessionOptions["refreshMode"]>,
): Promise<DropInSessionResponse | null> {
  const requestStartedAt = Date.now();
  const session = await dropinApi.getSession(facilityId);
  if (refreshMode !== "lifecycle" || session?.status !== "active") {
    return session;
  }

  const expiresAtMs = Date.parse(session.expires_at ?? "");
  const crossedExpiryWhileInFlight =
    Number.isFinite(expiresAtMs) &&
    requestStartedAt < expiresAtMs &&
    Date.now() >= expiresAtMs;
  return crossedExpiryWhileInFlight
    ? dropinApi.getSession(facilityId)
    : session;
}

export interface UseDropInSessionOptions {
  /**
   * Queue surfaces already reconcile from court-state events. They need one
   * read at the authoritative expiry, not another fixed polling loop.
   *
   * The active-session hub uses lifecycle mode: retain foreground polling for
   * independent-tab changes, but shorten the final interval so one read lands
   * exactly on the active admission's authoritative expiry.
   */
  refreshMode?: "poll" | "expiry" | "lifecycle";
}

/**
 * The player's current drop-in session at a facility, if any
 * (GET /facilities/{id}/drop-in/session → DropInSessionResponse | null).
 *
 * A non-null result means the player is already checked in and carries the
 * authoritative `expires_at`, payment method, and check-in time. A null result
 * is an honest "no active session"; the caller never fabricates one.
 *
 * Single hook per (query key, resource). Check-in and session-hub surfaces poll
 * to reconcile independent tabs. Queue surfaces instead reconcile from the
 * court-state stream and schedule one read at the server-owned expiry.
 */
export function useDropInSession(
  facilityId: string,
  { refreshMode = "poll" }: UseDropInSessionOptions = {},
) {
  return useQuery<DropInSessionResponse | null>({
    queryKey: queryKeys.dropin.session(facilityId),
    queryFn: () => fetchDropInSession(facilityId, refreshMode),
    enabled: Boolean(facilityId),
    staleTime: 0,
    refetchInterval:
      refreshMode === "poll"
        ? facilityId
          ? FOREGROUND_REFRESH_MS
          : false
        : (query) => {
            if (
              refreshMode === "lifecycle" &&
              query.state.data?.status === "pending_validation"
            ) {
              return FOREGROUND_REFRESH_MS;
            }
            const expiresAt = query.state.data?.expires_at;
            if (!expiresAt) return false;
            const expiresAtMs = new Date(expiresAt).getTime();
            if (!Number.isFinite(expiresAtMs)) return false;
            const remainingMs = expiresAtMs - Date.now();
            if (remainingMs <= 0) {
              return refreshMode === "lifecycle"
                ? FOREGROUND_REFRESH_MS
                : false;
            }
            return refreshMode === "lifecycle"
              ? Math.min(FOREGROUND_REFRESH_MS, remainingMs)
              : Math.min(MAX_TIMEOUT_MS, remainingMs);
          },
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: "always",
    refetchOnReconnect: "always",
  });
}
